/**
 * ライブアクティビティフィードのイベント判定（タスク54）。React に依存しない純関数。
 *
 * 既存ポーリング（/api/locations・/api/posts）の「前回との差分」からイベントを作る。
 * 新しい API は使わず、地図・公開中パネル・軌跡で既に見えている情報だけを使う
 * （visible=false の人はサーバーの応答に含まれないので、ここに来ることも無い）。
 *
 * - チェックイン: 前回いなかった人が現れた／前回の点から CHECKIN_GAP_SEC 以上空いて点が更新された
 *   （トラッキング中は約30秒ごとに点が更新されるので「更新されたら」では毎回出てしまう）
 * - 移動: 基準点から MOVE_THRESHOLD_M 以上離れた、かつ軌跡を公開している人だけ
 *   （応答に trail_visible は無いが、サーバーは trail_visible=true の人にだけ直近30分の軌跡を返すので
 *   `trail.length > 0` で判定できる）。基準点は「最後に移動を出した位置（または現れた位置）」。
 *   直前のポーリング位置と比べると、30秒で300m（時速36km）動かない限り出ず、徒歩の移動を拾えない。
 * - 同じ人の移動は MOVE_COOLDOWN_MS に1回まで。1回の差分で AGG_MIN 件以上なら「N人が移動中」にまとめる。
 * - 別のアプリから戻った直後は、下限時刻（minUpdatedAt / minCreatedAt）より古いものは出さない（タスク54.5）。
 *   状態は全員分進めるので、出さなかった古い変化が後から出ることもない。
 */
import type { Post, UserLocation } from '../api/client';
import { distanceMeters, nearestVenue } from '../map/venues';

export const MOVE_THRESHOLD_M = 300;
export const MOVE_COOLDOWN_MS = 5 * 60_000;
export const CHECKIN_GAP_SEC = 10 * 60;
export const AGG_MIN = 3;
/** 別のアプリから戻ったときに出す範囲（直近この秒数以内の出来事だけ・タスク54.5）。 */
export const RESUME_WINDOW_SEC = 120;
/** 「N人が移動中」に添える会場名の最大数。 */
const AGG_MAX_VENUES = 2;

/** 表示用のイベント。文言ではなくデータを持ち、表示時の言語で組み立てる。 */
export type FeedEvent =
  | { id: number; type: 'checkin'; name: string; venueId?: string }
  | { id: number; type: 'post'; name: string; venueId?: string }
  | {
      id: number;
      type: 'move';
      name: string;
      fromVenueId?: string;
      toVenueId?: string;
    }
  | { id: number; type: 'agg'; count: number; venueIds: string[] };

export type FeedEventType = FeedEvent['type'];

let nextId = 1;
function newId(): number {
  return nextId++;
}

/** 1人分の判定用の状態。 */
interface UserState {
  /** 最新点の時刻（epoch 秒）。 */
  updatedAt: number;
  /** 移動判定の基準点。 */
  anchorLat: number;
  anchorLng: number;
  /** 最後に移動を出した時刻（ms）。まだなら null。 */
  lastMoveMs: number | null;
}

/** 位置の判定状態（user_id → 状態）。null は「まだ基準が無い」。 */
export type LocationState = Map<string, UserState>;

function venueIdAt(lat: number, lng: number): string | undefined {
  return nearestVenue(lat, lng)?.id;
}

/**
 * 位置の差分からイベントを作る。
 * @param prev 前回の状態。null なら基準を作るだけでイベントは出さない（初回）
 * @param minUpdatedAt 指定時は、最新点がこれより古い人のイベントを出さない（epoch 秒・別のアプリから戻った直後）
 * @returns events と次回に渡す状態
 */
export function detectLocationEvents(
  prev: LocationState | null,
  users: readonly UserLocation[],
  nowMs: number,
  minUpdatedAt?: number,
): { events: FeedEvent[]; next: LocationState } {
  const next: LocationState = new Map();
  const events: FeedEvent[] = [];
  const moves: { name: string; fromVenueId?: string; toVenueId?: string }[] =
    [];

  for (const u of users) {
    const { lat, lng, updated_at } = u.latest;
    const before = prev?.get(u.user_id);
    const recent = minUpdatedAt === undefined || updated_at >= minUpdatedAt;
    // 新しく現れた人（または基準作成時）は、今の位置を基準点にする
    const fresh: UserState = {
      updatedAt: updated_at,
      anchorLat: lat,
      anchorLng: lng,
      lastMoveMs: null,
    };

    if (!prev) {
      next.set(u.user_id, fresh);
      continue;
    }
    if (!before) {
      next.set(u.user_id, fresh);
      if (!recent) {
        continue;
      }
      events.push({
        id: newId(),
        type: 'checkin',
        name: u.display_name,
        venueId: venueIdAt(lat, lng),
      });
      continue;
    }
    if (updated_at <= before.updatedAt) {
      // 点が更新されていない
      next.set(u.user_id, before);
      continue;
    }
    if (updated_at - before.updatedAt >= CHECKIN_GAP_SEC) {
      // 間を空けての更新はチェックイン（移動にも当たる場合もチェックインだけ）
      next.set(u.user_id, { ...fresh, lastMoveMs: before.lastMoveMs });
      if (!recent) {
        continue;
      }
      events.push({
        id: newId(),
        type: 'checkin',
        name: u.display_name,
        venueId: venueIdAt(lat, lng),
      });
      continue;
    }
    if (u.trail.length === 0) {
      // 軌跡を公開していない人は移動を出さない。基準点は追従させ、後で公開に切り替えたときに飛ばないようにする
      next.set(u.user_id, {
        ...before,
        updatedAt: updated_at,
        anchorLat: lat,
        anchorLng: lng,
      });
      continue;
    }
    const dist = distanceMeters(before.anchorLat, before.anchorLng, lat, lng);
    const cooling =
      before.lastMoveMs !== null &&
      nowMs - before.lastMoveMs < MOVE_COOLDOWN_MS;
    if (dist >= MOVE_THRESHOLD_M && !recent) {
      // 離れている間に済んだ移動は出さず、基準点だけ今の位置に進める
      next.set(u.user_id, {
        ...before,
        updatedAt: updated_at,
        anchorLat: lat,
        anchorLng: lng,
      });
      continue;
    }
    if (dist >= MOVE_THRESHOLD_M && !cooling) {
      next.set(u.user_id, {
        updatedAt: updated_at,
        anchorLat: lat,
        anchorLng: lng,
        lastMoveMs: nowMs,
      });
      moves.push({
        name: u.display_name,
        fromVenueId: venueIdAt(before.anchorLat, before.anchorLng),
        toVenueId: venueIdAt(lat, lng),
      });
      continue;
    }
    // しきい値未満・クールダウン中は基準点を据え置く（距離を積み上げて判定する）
    next.set(u.user_id, { ...before, updatedAt: updated_at });
  }

  if (moves.length >= AGG_MIN) {
    const venueIds: string[] = [];
    for (const mv of moves) {
      if (mv.toVenueId && !venueIds.includes(mv.toVenueId)) {
        venueIds.push(mv.toVenueId);
      }
      if (venueIds.length >= AGG_MAX_VENUES) {
        break;
      }
    }
    events.push({ id: newId(), type: 'agg', count: moves.length, venueIds });
  } else {
    for (const mv of moves) {
      events.push({ id: newId(), type: 'move', ...mv });
    }
  }

  return { events, next };
}

/**
 * 投稿の差分からイベントを作る。
 * @param prevIds 前回の post_id の集合。null なら基準を作るだけ
 * @param minCreatedAt 指定時は、これより古い投稿を出さない（epoch 秒・別のアプリから戻った直後）
 */
export function detectPostEvents(
  prevIds: ReadonlySet<string> | null,
  posts: readonly Post[],
  minCreatedAt?: number,
): { events: FeedEvent[]; next: Set<string> } {
  const next = new Set(posts.map((p) => p.post_id));
  if (!prevIds) {
    return { events: [], next };
  }
  const events: FeedEvent[] = posts
    .filter((p) => !prevIds.has(p.post_id))
    .filter((p) => minCreatedAt === undefined || p.created_at >= minCreatedAt)
    // 古い順に出す（新しいものほど後で積まれ、上に表示される）
    .sort((a, b) => a.created_at - b.created_at)
    .map((p) => ({
      id: newId(),
      type: 'post' as const,
      name: p.display_name,
      venueId: venueIdAt(p.lat, p.lng),
    }));
  return { events, next };
}
